import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { useConfig } from "./ConfigProvider";
import { useTunnelData } from "./TunnelContext";
import devices from "./devices";
import { darken } from "polished";


const StepsDisplay = ({ step, disabled }) => {
  const config = useConfig();
  const { reinitTunnel } = useTunnelData();

  let usedSteps = [
    { txt: config.stepsLabels.club, path: "/", short: config.stepsShortLabels.club },
    { txt: config.stepsLabels.contact, path: "/user", short: config.stepsShortLabels.contact },
    { txt: config.stepsLabels.plans, path: "/plans", short: config.stepsShortLabels.plans },
    { txt: config.stepsLabels.contract, path: "/contract", short: config.stepsShortLabels.contract },
    { txt: config.stepsLabels.payment, path: "/sepa", short: config.stepsShortLabels.payment },
  ];
  if (config.plans.beforeUser) {
    // Inversion de /user et /plans
    [usedSteps[1], usedSteps[2]] = [usedSteps[2], usedSteps[1]];
  }
  if (config.onlyOneClub || (config.navigation && !config.navigation.allowChangeClub)) {
    usedSteps = usedSteps.slice(1);
  }

  if (config.photo) {
    const photoStepObj = { txt: config.stepsLabels.photo, path: "/photo", short: config.stepsShortLabels.photo };

    if (config.photo.step === 'end') {
      usedSteps.splice(usedSteps.length +1, 0, photoStepObj);
    } else {
      const stepIndex = usedSteps.findIndex((step) => step.short === config.photo.step);
      usedSteps.splice(stepIndex + 1, 0, photoStepObj);
    }
  }

  const foundIndex = usedSteps.findIndex((f) => f.txt === step);
  const history = useHistory();

  const onClick = (index, step) => {
    if (disabled) return false;
    if (index >= foundIndex) return null;
    if (!step.path) {
      console.warn("Missing path for step", step);
      return null;
    }
    history.push(step.path);
  };

  const CompleteStyledList = (
    <StyledList className="stepsContainer" theme={config.theme}>
      {usedSteps.map((s, i) => {
        const filled = i <= foundIndex;

        const span = config.steps.display.showShortNames ? <span>{s.short}</span> : <span>&nbsp;</span>;
        return (
          <StyledLi key={`step_${i}`} onClick={(e) => onClick(i, s)} filled={filled} theme={config.theme}>
            <div>{i + 1}</div>
            {span}
          </StyledLi>
        );
      })}
    </StyledList>
  );

  useEffect(() => {
    if (config.navigation?.reinitAuto && foundIndex > 0) {
      const interval = setInterval(() => {
        reinitTunnel();
      }, config.navigation?.reinitAutoSecs * 1000);

      return () => clearInterval(interval);
    }
  }, [config.navigation?.reinitAuto, config.navigation?.reinitAutoSecs, reinitTunnel, foundIndex]);

  // Ajout breadcrumb pour mode "line"
  const breadcrumbSeparator = " > ";

  if (config.navigation?.display === "none") {
    return (
      <></>
    );
  }
  else if (config.navigation?.display === "line") {
    return (
      <nav className="breadcrumb-line">
        {usedSteps.map((s, i) => {
          const isActive = i === foundIndex;
          const spanClassName = isActive ? "activated" : i < foundIndex ? "completed" : "";
          return (
            <React.Fragment key={`breadcrumb_${i}`}>
              <span
                className={spanClassName}
                onClick={() => !disabled && i < foundIndex && s.path && history.push(s.path)}
                style={{ cursor: !disabled && i < foundIndex && s.path ? "pointer" : "default" }}
              >
                {config.steps.display.showShortNames ? s.short : s.txt}
              </span>
              {i < usedSteps.length - 1 && <span className="breadcrumb-separator">{breadcrumbSeparator}</span>}
            </React.Fragment>
          );
        })}
      </nav>
    );
  }
  else { // Cas par défaut : "steps"
    if (config.navigation?.reinitAuto) {
      return (
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <div style={{ flex: "1 1 20%" }}>
            <StyledLi key={`step_restart`} style={{ cursor: "pointer" }} onClick={reinitTunnel}>
              <div>
                <img src="/icons/restart.svg" alt="Restart" />
              </div>
              <span>Recommencer</span>
            </StyledLi>
          </div>
          <div style={{ flex: "4 1 80%" }}>{CompleteStyledList}</div>
        </div>
      );
    } else {
      return CompleteStyledList;
    }
  }

  //TODO unclickable if there is a stripe payment or thing like that
};

export default StepsDisplay;

const StyledList = styled.ol`
  position: relative;
  flex-direction: row;
  justify-content: space-between;
  padding: 0.2rem 1.5rem;
  @media (${devices.tablet.query}) {
    padding: 1rem calc((100% - ${devices.tablet.size}) / 2 + 1.5rem);
  }
  &::before {
    content: "";
    width: calc(100% - 4rem);
    height: 0.45rem;
    background-color: ${(props) => props.theme.stepsDisplay?.color?.main ?? "black"};
    position: absolute;
    left: 2rem;
    top: calc(45%);
    @media (${devices.tablet.query}) {
      top: calc(32%);
      width: calc(${devices.tablet.size} - 5rem);
      left: calc((100% - ${devices.tablet.size}) / 2 + 2rem);
    }
  }
`;

const StyledLi = styled.li`
  position: relative;
  flex-direction: column;
  align-items: center;
  div {
    height: 2.125rem;
    width: 2.125rem;
    border-radius: 100rem;
    border: 2px solid ${(props) => props.theme.stepsDisplay?.color?.main ?? "black"};
    font-weight: 700;
    font-size: 1.375rem;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    ${(props) => {
      if (props.filled) {
        return `
          cursor: pointer;
          background-color: ${props.theme.stepsDisplay?.color?.bgFilled ?? "black"};
          color: ${props.theme.stepsDisplay?.color?.textFilled ?? "white"};
          &:hover{
            border-color: ${darken(0.15, props.theme.stepsDisplay?.color?.main ?? "black")};
            background-color: ${darken(0.15, props.theme.stepsDisplay?.color?.bgFilled ?? "black")};
          }

          pointer-events: all;
        `;
      } else {
        return `
          color: ${props.theme.stepsDisplay?.color?.main ?? "black"};
          background-color: ${props.theme.stepsDisplay?.color?.bg ?? "white"};
        `;
      }
    }}
  }
  span {
    margin-top: 0.5rem;
    display: none;
    color: ${(props) => props.theme.stepsDisplay?.color?.subText ?? "black"};
    @media (${devices.tablet.query}) {
      display: block;
    }
    ${(props) => {
      if (props.filled) {
        return `font-weight:700;`;
      }
    }}
  }
`;
